import type {
  AccountSummary,
  Order,
  Position,
  PositionQueryParams,
  PositionsListResponse,
} from "@/types/execution.types";
import {
  API_BASE_URL,
  buildQueryString,
  fetchWithTimeout,
  handleResponse,
} from "./shared";

// ============================================================================
// Positions API (CQG Integration)
// ============================================================================

export const positionsApi = {
  /**
   * List positions with optional filters
   */
  async list(params?: PositionQueryParams): Promise<PositionsListResponse> {
    const queryParams = params
      ? {
          symbol: params.symbol,
          side: params.side,
          brokerId: params.brokerId,
          accountId: params.accountId,
          strategyId: params.strategyId,
        }
      : undefined;

    const queryString = buildQueryString(queryParams);
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/positions${queryString}`
    );
    return handleResponse<PositionsListResponse>(response);
  },

  /**
   * Get a position by symbol
   */
  async getBySymbol(symbol: string, accountId?: string): Promise<Position> {
    const queryString = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/positions/${encodeURIComponent(symbol)}${queryString}`
    );
    return handleResponse<Position>(response);
  },

  /**
   * Get account summary
   */
  async getAccountSummary(accountId: string): Promise<AccountSummary> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/accounts/${accountId}/summary`
    );
    return handleResponse<AccountSummary>(response);
  },

  /**
   * Close a position (market order to flatten)
   */
  async closePosition(
    symbol: string,
    accountId: string,
    quantity?: number
  ): Promise<Order> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/positions/${encodeURIComponent(symbol)}/close`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accountId, quantity }),
      }
    );
    return handleResponse<Order>(response);
  },
};
